import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NxMessageToastService } from '@aposin/ng-aquila/message';
import { SortDirection, SortEvent } from '@aposin/ng-aquila/table';
import { AppConfig } from 'src/config/app.config';
import { AuthService } from '../core/auth/auth.service';
import { NotificationService } from '../core/notification.service';
import { Subscription } from 'rxjs';
import * as moment from 'moment';

@Component({
  selector: 'app-dealer-pfu-invoice',
  templateUrl: './dealer-pfu-invoice.component.html',
  styleUrls: ['./dealer-pfu-invoice.component.css']
})
export class DealerPfuInvoiceComponent implements OnInit, OnDestroy {

  /** Pagination */
  count: number = 0;
  pageNumber: number = 1;
  pageSize: number = 10;

  prevPage() { this.pageNumber--; this.getInvoiceList(); }
  nextPage() { this.pageNumber++; this.getInvoiceList(); }
  goToPage(n: number) { this.pageNumber = n; this.getInvoiceList(); }

  /** Sorting */
  sortTable(sort: SortEvent) {
    this.invoiceList = this.invoiceList.sort((a, b) => {
      if (sort.active in a && sort.active in b) {
        return this.compare(a[sort.active], b[sort.active], sort.direction);
      }
      return 0;
    });
  }

  compare(a: number | string | Date, b: number | string | Date, direction: SortDirection) {
    return (a < b ? -1 : 1) * (direction === 'asc' ? 1 : -1);
  }

  private _apiUrl: string;

  invoiceList: any[] = [];
  searchTerm: string = '';
  statusFilter: string = '';
  startDate: moment.Moment | null = null;
  endDate: moment.Moment | null = null;
  fileName: string = '';

  readonly months: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  /** Dispute comment modal */
  disputeModalOpen: boolean = false;
  disputeForm!: FormGroup;
  disputeInProgress: boolean = false;
  selectedInvoice: any = null;

  /** Review details modal */
  reviewModalOpen: boolean = false;

  private notifSub: Subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private appConfig: AppConfig,
    private fb: FormBuilder,
    private messageToastService: NxMessageToastService,
    private notifService: NotificationService
  ) {
    this._apiUrl = appConfig.baseUrl;
  }

  ngOnInit(): void {
    this.disputeForm = this.fb.group({
      disputeComment: ['', [Validators.required, Validators.maxLength(1000)]]
    });

    this.getInvoiceList();
    this.notifService.pollNow();

    this.notifSub.add(
      this.notifService.newNotifications$.subscribe(() => this.getInvoiceList())
    );
  }

  ngOnDestroy(): void {
    this.notifSub.unsubscribe();
  }

  getInvoiceList() {
    const from = this.startDate ? this.startDate.format('LL') : '';
    const to = this.endDate ? this.endDate.format('LL') : '';
    this.authService
      .getList(
        this._apiUrl +
        `/api/v1/PfuInvoice/dealer/list?pageNumber=${this.pageNumber}&pageSize=${this.pageSize}` +
        `&searchText=${this.searchTerm}&status=${this.statusFilter}&startDate=${from}&endDate=${to}`
      )
      .subscribe(
        (response) => {
          if (response) {
            this.invoiceList = response.body || [];
            const xPagination = response.headers.get('X-Pagination');
            var pagingProp = xPagination ? JSON.parse(xPagination) : null;
            this.count = pagingProp ? pagingProp.TotalCount : 0;
            this.notifService.pollNow();
          }
        },
        (error) => console.error(error)
      );
  }

  exportToExcel() {
    const from = this.startDate ? this.startDate.format('LL') : '';
    const to = this.endDate ? this.endDate.format('LL') : '';
    this.authService
      .getList(
        this._apiUrl +
        `/api/v1/PfuInvoice/dealer/list?pageNumber=-1&pageSize=10000` +
        `&searchText=${this.searchTerm}&status=${this.statusFilter}&startDate=${from}&endDate=${to}`
      )
      .subscribe(
        (response) => {
          if (response) {
            const rows = (response.body || []).map((item: any) => ({
              'Reference No.': item.referenceNo || '',
              'Invoice Period': `${this.months[item.invoiceMonth - 1]} ${item.invoiceYear}`,
              'Raised On': item.submittedOn ? new Date(item.submittedOn).toLocaleDateString('en-GB') : '',
              'AWP Comments': item.comments || '',
              'My Dispute Comment': item.disputeComment || ''
            }));
            import('xlsx').then(xlsx => {
              const ws = xlsx.utils.json_to_sheet(rows);
              const wb = xlsx.utils.book_new();
              xlsx.utils.book_append_sheet(wb, ws, 'PFU Invoices');
              this.fileName = `PFUInvoices_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.xlsx`;
              xlsx.writeFile(wb, this.fileName);
            });
          }
        },
        (error) => console.error(error)
      );
  }

  badgeType(status: string): string {
    switch (status) {
      case 'Paid': return 'positive';
      case 'Rejected': return 'critical';
      case 'Under Process': return 'active';
      default: return 'active';
    }
  }

  downloadInvoice(invoiceId: number, referenceNo: string) {
    this.authService
      .export(this._apiUrl + `/api/v1/PfuInvoice/download/${invoiceId}`)
      .subscribe((data) => saveAs(data, `${referenceNo}_pfu_invoice.pdf`));
  }

  openDisputeModal(item: any) {
    this.selectedInvoice = item;
    this.disputeForm.reset();
    if (item.disputeComment) {
      this.disputeForm.patchValue({ disputeComment: item.disputeComment });
    }
    this.disputeModalOpen = true;
  }

  closeDisputeModal() {
    this.disputeModalOpen = false;
    this.selectedInvoice = null;
  }

  openReviewModal(item: any) {
    this.selectedInvoice = item;
    this.reviewModalOpen = true;
  }

  closeReviewModal() {
    this.reviewModalOpen = false;
    this.selectedInvoice = null;
  }

  submitDisputeComment() {
    this.disputeForm.markAllAsTouched();
    if (this.disputeForm.invalid) return;

    this.disputeInProgress = true;
    this.authService
      .put(this._apiUrl + `/api/v1/PfuInvoice/dispute/${this.selectedInvoice.id}`, {
        disputeComment: this.disputeForm.value.disputeComment
      })
      .subscribe(
        () => {
          this.disputeInProgress = false;
          this.disputeModalOpen = false;
          this.messageToastService.open('Dispute comment submitted successfully.', { context: 'success', duration: 4000 });
          this.getInvoiceList();
        },
        (error) => {
          this.disputeInProgress = false;
          console.error(error);
          this.messageToastService.open('Failed to submit comment. Please try again.', { context: 'info', duration: 4000 });
        }
      );
  }
}
